import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useAppSelector } from 'careand-redux/store';
import { Physician, SubscriberCountSnapshot } from 'generated/api';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import CalendarUtils from 'services/CalendarUtils';
import ClinicUtils from 'services/ClinicUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import './style.scss';

/**
 * Why a practitioner still holding subscribers can't keep them — the rows
 * admins have to move to someone else. Mirrors the backend's isValidPrimary
 * (minus the role/type check, which the FE can't resolve).
 */
function rosterProblem(physician: Physician | undefined): string | undefined {
  if (!physician) return 'No staff record';
  if (physician.inactive) return 'Departed';
  if (physician.locum) return 'Locum, not a primary';
  if (physician.is_not_valid_primary || physician.intern_of) return 'Not a valid primary';
  return undefined;
}

type Row = { physicianId: string; count: number; physician?: Physician };

/**
 * Subscribers per primary practitioner from the newest daily snapshot.
 * All clinics lists everyone plus the subscribers with no practitioner; a
 * clinic lists the practitioners at it; a practitioner shows their clinic's
 * list with their row highlighted.
 */
const CurrentRoster = ({
  snapshot,
  loaded,
  clinicId,
  physicianId,
}: {
  snapshot?: SubscriberCountSnapshot;
  loaded: boolean;
  clinicId?: string;
  physicianId?: string;
}) => {
  const physicians = useAppSelector((state) => state.physicians.byId);
  const clinics = useAppSelector((state) => state.resource.clinics);

  // Practitioner records can still name a closed clinic (e.g. a departed
  // Lawrence Park NP who still holds patients); count them where that clinic
  // went, since the closed one is no longer a filter.
  const clinicOf = useCallback(
    (physician?: Physician) => ClinicUtils.resolveForwardedClinicId(physician?.clinic_id, clinics),
    [clinics],
  );

  const scopeClinicId = clinicId ?? (physicianId ? clinicOf(physicians[physicianId]) : undefined);
  const scopeClinic = clinics.find(({ id }) => id === scopeClinicId);

  const { rows, total, unassigned } = useMemo(() => {
    const all: Row[] = (snapshot?.physicians ?? []).map(({ physician_id, count }) => ({
      physicianId: physician_id!,
      count: count ?? 0,
      physician: physicians[physician_id!],
    }));
    const unassigned = snapshot?.unassigned ?? 0;
    const total = _.sumBy(all, 'count') + unassigned;

    let rows = scopeClinicId
      ? all.filter(({ physician }) => clinicOf(physician) === scopeClinicId)
      : all;
    // A selected practitioner with no subscribers isn't in the snapshot at
    // all; still show their zero.
    if (physicianId && !rows.some((row) => row.physicianId === physicianId)) {
      rows = [...rows, { physicianId, count: 0, physician: physicians[physicianId] }];
    }
    rows = _.orderBy(
      rows,
      ['count', (row) => PhysicianUtils.getName(row.physician)],
      ['desc', 'asc'],
    );

    return { rows, total, unassigned };
  }, [snapshot, physicians, clinicOf, scopeClinicId, physicianId]);

  if (!snapshot) {
    return loaded ? <span className="current-roster-summary">No roster snapshot yet</span> : null;
  }

  const asOf = moment
    .unix(snapshot.date!)
    .tz(CalendarUtils.getTargetTimezone())
    .format('MMM D, YYYY');
  const scopeTotal = _.sumBy(rows, 'count');
  const selectedCount = rows.find((row) => row.physicianId === physicianId)?.count ?? 0;
  const showClinicColumn = !scopeClinicId && clinics.length > 1;

  const tableRows: React.ReactNode[][] = rows.map(({ physician, count }) => {
    const problem = rosterProblem(physician);
    const name = (
      <span className="roster-name">
        {PhysicianUtils.getName(physician) ?? 'Unknown practitioner'}
        {problem && <span className="roster-flag">{problem}</span>}
      </span>
    );
    const clinicName = clinics.find((clinic) => clinic.id === clinicOf(physician))?.name ?? '';
    return showClinicColumn ? [name, clinicName, count] : [name, count];
  });
  const rowClassNames: string[] = rows.map(({ physicianId: id }) =>
    id === physicianId ? 'roster-selected' : '',
  );

  if (!scopeClinicId) {
    tableRows.push(
      showClinicColumn
        ? ['No practitioner assigned', '', unassigned]
        : ['No practitioner assigned', unassigned],
    );
    rowClassNames.push('');
  }
  const totalLabel = scopeClinic ? `Total at ${scopeClinic.name}` : 'Total';
  tableRows.push(
    showClinicColumn
      ? [totalLabel, '', scopeClinicId ? scopeTotal : total]
      : [totalLabel, scopeClinicId ? scopeTotal : total],
  );
  rowClassNames.push('roster-total');

  return (
    <div className="current-roster">
      <span className="current-roster-summary">
        As of {asOf}
        {physicianId ? (
          <>
            {' · '}
            {PhysicianUtils.getName(physicians[physicianId]) ?? 'Unknown practitioner'}:{' '}
            <b>{selectedCount}</b>
          </>
        ) : null}
        {scopeClinic ? (
          <>
            {' · '}
            <b>{scopeTotal}</b> at {scopeClinic.name}
          </>
        ) : null}
        {' · '}
        <b>{total}</b> active subscribers across all clinics
      </span>
      <ATable
        headers={
          showClinicColumn
            ? ['Practitioner', 'Clinic', 'Subscribers']
            : ['Practitioner', 'Subscribers']
        }
        rows={tableRows}
        rowClassNames={rowClassNames}
      />
    </div>
  );
};

export default CurrentRoster;
